import type { z } from "zod";

/** The session ran out. The query client sends the browser to sign-in. */
export class SessionExpiredError extends Error {
  override name = "SessionExpiredError";
  constructor() {
    super("Your session has expired");
  }
}

export class ApiError extends Error {
  override name = "ApiError";
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function failureMessage(res: Response): Promise<string> {
  const fallback = `The server answered ${res.status}`;
  try {
    const body: { error?: unknown } = await res.json();
    return typeof body.error === "string" ? body.error : fallback;
  } catch {
    return fallback;
  }
}

/**
 * GET an API route and parse it. A failure may come from a proxy or the app shell, so
 * neither the status nor the body is assumed to be JSON.
 */
export async function getJson<S extends z.ZodType>(
  url: string,
  schema: S,
  fetcher: (url: string) => Promise<Response> = fetch,
): Promise<z.output<S>> {
  const res = await fetcher(url);
  if (res.status === 401) throw new SessionExpiredError();
  if (!res.ok) throw new ApiError(await failureMessage(res), res.status);
  if (!res.headers.get("Content-Type")?.includes("application/json")) {
    throw new ApiError("The server did not answer with data", res.status);
  }
  return schema.parse(await res.json());
}
